/* =========================================================================
   FRIENDLY PLAY — study catalog (PLAN-gameplay-round2.md Part 2b).

   PURE logic: which study areas exist, how many questions each holds per
   player-facing level, and the pool a chosen (area × level) serves. No DOM,
   no network, no money — friendlyController.ts wires it to the screen.

   v1 SOURCE = the BUNDLED packs (PACK–PACK5 + HARD_PACK). The plan's
   destination backbone is the DB catalog (app.usp_Categories_Catalog →
   GET /api/game/catalog), deferred to Phase 3 with the rest of server-served
   questions; the shape here (area · level · count) is deliberately the same
   one that endpoint will return, so the picker UI doesn't change when the
   source upgrades.

   LEVELS. The client's difficulty tag is engine.Question.hardness (absent = 1
   easy/popular · 2 = a little harder · 3 = heavy) and the HARD_PACK is Level 3
   wholesale (its questions carry no tag — membership IS the tag, the same way
   gameController treats ids >= 3001). Those map 1:1 onto the three
   player-facing tiers, matching the existing 🔥 hard-mode vocabulary. DB
   Level-0 ("playable at all levels") has no bundled equivalent yet; when the
   catalog endpoint lands, Level-0 rows fold into every tier's count.

   AREAS. Raw pack categories are folded through quests.topicOf, which already
   groups the one-off Bible-story categories (Noah, Jonah, Moses, …) under
   'Scripture'. Reusing it keeps ONE definition of "what topic is this?" in the
   app rather than a second, drifting copy.
   ========================================================================= */
import type { Question } from './engine';
import { PACK, PACK2, PACK3, PACK4, PACK5, type Pack } from './data/packs';
import { HARD_PACK } from './data/hardPack';
import { topicOf } from './quests';

/** Player-facing tiers. 3 is the 🔥 tier (the hard pack + hardness-3 items). */
export type FriendlyLevel = 1 | 2 | 3;
/** A picker choice: one tier, or every tier at once. */
export type LevelChoice = FriendlyLevel | 'all';

/** Questions served in one study set before the "Keep studying / Done" seam. */
export const FRIENDLY_SET_SIZE = 10;

/** XP per correct answer (2026-08-03, Eric: track XP here — still no money). Same
    per-question rate as scored play's server-authoritative /game/complete formula
    (GameEndpoints.cs: XP = correct*100); kept as one named constant so the client's
    end-card display and the server's authoritative award (which recomputes this
    independently — see usp_Stats_AwardXp, db/038) can't silently drift apart. */
export const FRIENDLY_XP_PER_CORRECT = 100;

export const LEVEL_CHOICES: readonly LevelChoice[] = [1, 2, 3, 'all'];

/** Short label for a tier ("Level 3 🔥" mirrors the hard-mode wording). */
export function levelLabel(choice: LevelChoice): string {
  if (choice === 'all') return 'All levels';
  return choice === 3 ? 'Level 3 🔥' : `Level ${choice}`;
}

/* --------------------------------- icons ---------------------------------
   app.Categories has no icon column (db/001), so v1 maps them client-side.
   The five round icons already in ROUNDS (✝ 📖 🎬 🇺🇸 🌹) are reused wherever
   an area lines up; the rest are new, in the same one-glyph register — except
   Popes (2026-08-04, Eric: should read as St. Peter's crossed keys, one gold
   one silver — Unicode has no single crossed-keys glyph, so 🔑+🗝️ stand in
   for the two). */
export const AREA_ICONS: Readonly<Record<string, string>> = {
  Scripture: '📖', // ROUNDS "Bible Stories"
  'The Chosen': '🎬', // ROUNDS "The Chosen — Season 1"
  'American Catholics': '🇺🇸', // ROUNDS "American Catholics"
  'The Blessed Mother': '🌹', // ROUNDS "The Blessed Mother"
  Doctrine: '✝', // ROUNDS "Catholic Trivia"
  History: '🏛️',
  Popes: '🔑🗝️',
  Liturgy: '🕯️',
  Saints: '😇',
  Cathedrals: '⛪',
  'Saint or Metal Band?': '🎸',
  'Vatican or Fiction?': '🇻🇦',
};

export const DEFAULT_AREA_ICON = '✝';

export function areaIcon(area: string): string {
  return AREA_ICONS[area] ?? DEFAULT_AREA_ICON;
}

/* ------------------------------- study items ------------------------------- */

/** One bundled question, tagged with the area + tier the picker files it under. */
export interface StudyItem {
  question: Question;
  area: string;
  level: FriendlyLevel;
}

/** The player-facing tier of a bundled question. `fromHardPack` questions are
    Level 3 by membership (they carry no hardness tag). PURE. */
export function levelOf(q: Question, fromHardPack = false): FriendlyLevel {
  if (fromHardPack) return 3;
  const h = q.hardness ?? 1;
  return h === 3 ? 3 : h === 2 ? 2 : 1;
}

/** Flatten packs into tagged study items. Duplicate ids are dropped (first
    occurrence wins) so a question that ever appears in two packs can't inflate
    a count or be served twice in one set. PURE. */
export function studyItemsFrom(packs: readonly Pack[], hardPacks: readonly Pack[] = []): StudyItem[] {
  const seen = new Set<number>();
  const out: StudyItem[] = [];
  const add = (p: Pack, hard: boolean): void => {
    for (const q of p.questions) {
      if (seen.has(q.id)) continue;
      seen.add(q.id);
      out.push({ question: q, area: topicOf(q.category) || q.category, level: levelOf(q, hard) });
    }
  };
  packs.forEach((p) => add(p, false));
  hardPacks.forEach((p) => add(p, true));
  return out;
}

/* ------------------------------- study areas ------------------------------- */

/** One picker row: the area, its icon, and its per-tier question counts. */
export interface StudyArea {
  name: string;
  icon: string;
  /** Questions available in each player-facing tier. */
  levels: Record<FriendlyLevel, number>;
  /** Questions in the area across all tiers. */
  total: number;
}

/** Aggregate study items into picker rows, richest area first (name breaks
    ties, so the order is stable between renders). PURE. */
export function buildStudyAreas(items: readonly StudyItem[]): StudyArea[] {
  const byName = new Map<string, StudyArea>();
  for (const it of items) {
    let row = byName.get(it.area);
    if (!row) {
      row = { name: it.area, icon: areaIcon(it.area), levels: { 1: 0, 2: 0, 3: 0 }, total: 0 };
      byName.set(it.area, row);
    }
    row.levels[it.level]++;
    row.total++;
  }
  return Array.from(byName.values()).sort((a, b) => b.total - a.total || a.name.localeCompare(b.name));
}

/** Questions a row offers for one picker choice. PURE. */
export function countFor(area: StudyArea, choice: LevelChoice): number {
  return choice === 'all' ? area.total : area.levels[choice];
}

/** The question pool for a chosen area × level, in bundled order (RoundEngine
    shuffles and slices it). An unknown area or an empty tier yields []. PURE. */
export function poolFor(items: readonly StudyItem[], area: string, choice: LevelChoice): Question[] {
  return items
    .filter((it) => it.area === area && (choice === 'all' || it.level === choice))
    .map((it) => it.question);
}

/** True once every question in an area × level's pool has been served at least
    once (checked against a caller-supplied "seen" id set — friendlyController
    owns what "seen" means and where it persists). An empty/unavailable tier is
    never "done" — its chip is disabled instead. PURE. */
export function isFullyServed(
  items: readonly StudyItem[],
  area: string,
  choice: LevelChoice,
  seenIds: ReadonlySet<number>,
): boolean {
  const pool = poolFor(items, area, choice);
  return pool.length > 0 && pool.every((q) => seenIds.has(q.id));
}

/** End-card "Next" seam — a different study area to try, cycling through
    `areas` (same stable order the picker renders) after whichever one the
    player just finished. Keeps the same level choice if the new area still
    has questions there, otherwise falls back to "All levels" (always
    non-empty for any listed area). Wraps around; a single-area catalog
    returns that same area rather than nothing. PURE. */
export function nextStudyArea(
  areas: readonly StudyArea[],
  currentArea: string,
  currentChoice: LevelChoice,
): { area: string; choice: LevelChoice } | null {
  if (areas.length === 0) return null;
  const i = areas.findIndex((a) => a.name === currentArea);
  const next = areas[(i + 1) % areas.length] ?? areas[0];
  const choice = countFor(next, currentChoice) > 0 ? currentChoice : 'all';
  return { area: next.name, choice };
}

/* -------------------------------- copy bits -------------------------------- */

/** Session header — "Doctrine · Level 2". PURE. */
export function sessionLabel(area: string, choice: LevelChoice): string {
  return `${area} · ${levelLabel(choice)}`;
}

/** End-card line — "8 of 10 — Doctrine, Level 2". Correct-count ONLY: Friendly
    Play never shows a dollar amount, a delta, or a bank. PURE. */
export function scoreLine(correct: number, total: number, area: string, choice: LevelChoice): string {
  return `${correct} of ${total} — ${area}, ${levelLabel(choice)}`;
}

/** A warm, non-scoring word for how the set went. PURE. */
export function encouragement(correct: number, total: number): string {
  if (total <= 0) return 'Come study any time.';
  const pct = correct / total;
  if (pct === 1) return 'Every one — beautifully done.';
  if (pct >= 0.7) return 'Well studied. Keep going!';
  if (pct >= 0.4) return "You're learning — that's what this is for.";
  return 'These are the ones worth studying. Try them again.';
}

/* ------------------------------ bundled source ------------------------------ */

let cachedItems: StudyItem[] | null = null;

/** Every bundled study item (memoized — the packs are static). */
export function bundledStudyItems(): StudyItem[] {
  if (!cachedItems) cachedItems = studyItemsFrom([PACK, PACK2, PACK3, PACK4, PACK5], [HARD_PACK]);
  return cachedItems;
}

/** The picker's rows, computed from the bundled packs. */
export function bundledStudyAreas(): StudyArea[] {
  return buildStudyAreas(bundledStudyItems());
}
